// File: src/pages/admin/PickupPending.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Stack,
  IconButton,
  InputAdornment,
  Fade,
  LinearProgress,
  Button,
} from '@mui/material';
import {
  Refresh,
  CheckCircle,
  Search,
  LocalShipping,
  Close,
  QrCode,
  ContentCopy,
  Image as ImageIcon,
  Store,
  Person,
  AttachMoney,
} from '@mui/icons-material';
import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import {
  useListOrdersQuery,
  useUpdateOrderStatusMutation,
  ORDER_STATUS_OPTIONS,
  Order,
  OrderItem,
} from '../../features/admin/OrderlistApi.ts';

// Helper functions (keep all your existing helpers)
const getStatusText = (status: number): string => {
  const option = ORDER_STATUS_OPTIONS.find(opt => opt.value === status);
  return option ? option.label : `Status ${status}`;
};

const getStatusColor = (status: number): string => {
  const option = ORDER_STATUS_OPTIONS.find(opt => opt.value === status);
  return option ? option.color : '#9e9e9e';
};

const formatDate = (dateString: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString();
};

const formatDateTime = (dateString: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString();
};

const formatCurrency = (amount: string | number | undefined) => {
  const n = Number(amount || 0);
  if (Number.isNaN(n)) return `AED ${amount}`;
  return `AED ${n.toFixed(2)}`;
};

// Helper to get product image URL
const getProductImageUrl = (imagePath: string | null | undefined): string => {
  if (!imagePath) return '';
  
  const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
  
  if (cleanPath.startsWith('http')) {
    return cleanPath;
  }
  
  if (cleanPath.includes('images/')) {
    return `https://fftestservice.sarsetech.com/${cleanPath}`;
  }
  
  return `https://fftestservice.sarsetech.com/images/${cleanPath}`;
};

// Helper to get warehouse name from order item
const getWarehouseName = (item: any): string => {
  if (item.warehouse_name) return item.warehouse_name;
  if (item.warehouse && item.warehouse.name) return item.warehouse.name;
  if (item.warehouse_name_text) return item.warehouse_name_text;
  return 'Unknown Warehouse';
};

// Helper to get delivery agent info
const getDeliveryAgentInfo = (order: Order) => {
  const agent = (order as any).delivery_agent;
  if (agent) {
    return agent.first_name || agent.name || agent.user_name || 'Delivery Agent';
  }
  return order.delivery_agent_name || 'Not Assigned';
};

const PickupPending = () => {
  const [orderId, setOrderId] = useState<string>('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [processingOrder, setProcessingOrder] = useState<Order | null>(null);
  const [isLoadingOrder, setIsLoadingOrder] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info',
  });

  // Add a ref for the input field
  const orderIdInputRef = useRef<HTMLInputElement>(null);

  // Use the existing orders API
  const { data: ordersData, isLoading: isLoadingOrders, refetch: refetchOrders } = useListOrdersQuery();
  const [updateOrderStatus] = useUpdateOrderStatusMutation();

  const allOrders = ordersData?.orders || [];

  // Focus on the input field when component mounts
  useEffect(() => {
    // Small delay to ensure the component is fully rendered
    const timer = setTimeout(() => {
      if (orderIdInputRef.current) {
        orderIdInputRef.current.focus();
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Auto-focus after successful update
  useEffect(() => {
    if (!isUpdating && !isLoadingOrder && selectedOrder && selectedOrder.status === 3) {
      // Focus on input field after successful update
      const timer = setTimeout(() => {
        if (orderIdInputRef.current) {
          orderIdInputRef.current.focus();
          orderIdInputRef.current.select();
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [selectedOrder, isUpdating, isLoadingOrder]);

  // Auto-update to Pickup Pending when processing order is found
  useEffect(() => {
    if (processingOrder && processingOrder.status !== 3 && !isUpdating) {
      const timer = setTimeout(() => {
        handleAutoUpdateToPickupPending(processingOrder);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [processingOrder, isUpdating]);

  // Auto-fetch when order ID changes
  useEffect(() => {
    if (orderId && orderId.length >= 3 && !isLoadingOrder) {
      const timer = setTimeout(() => {
        if (!/^0+$/.test(orderId)) {
          fetchOrderDetails(orderId);
        }
      }, 800);
      
      return () => clearTimeout(timer);
    }
  }, [orderId]);

  // Function to fetch order details from existing orders list
  const fetchOrderDetails = async (orderIdStr: string) => {
    try {
      const orderIdNum = parseInt(orderIdStr);
      if (isNaN(orderIdNum) || orderIdNum <= 0) {
        showSnackbar('Please enter a valid order number', 'error');
        return;
      }
      
      setIsLoadingOrder(true);
      setProcessingOrder(null);
      
      // First, refresh the orders list to get latest data
      await refetchOrders();
      
      // Find the order in the list
      const foundOrder = allOrders.find((order: Order) => order.id === orderIdNum);
      
      if (foundOrder) {
        setProcessingOrder(foundOrder);
        showSnackbar(`Order #${orderIdStr} found successfully!`, 'success');
      } else {
        showSnackbar(`Order #${orderIdStr} not found in current orders`, 'error');
      }
      
    } catch (err: any) {
      console.error('Error fetching order:', err);
      showSnackbar('Error fetching order details', 'error');
    } finally {
      setIsLoadingOrder(false);
    }
  };

  // Auto-update to Pickup Pending
  const handleAutoUpdateToPickupPending = async (order: Order) => {
    try {
      setIsUpdating(true);
      
      const updateData = {
        order_id: order.id,
        status: 3, // Pickup Pending
        comment: `Status auto-updated to Pickup Pending via scanner`,
      };

      const result = await updateOrderStatus(updateData).unwrap();
      
      // Refresh orders to get updated status
      await refetchOrders();
      
      // Update the order with new status and set as selected order
      const updatedOrder = { ...order, status: 3, status_text: 'Pickup Pending' };
      setSelectedOrder(updatedOrder);
      setProcessingOrder(null);
      
      showSnackbar(`✅ Order #${order.id} updated to Pickup Pending!`, 'success');
      
      // Clear input field but keep order displayed
      setOrderId('');
      
    } catch (err: any) {
      console.error('Failed to update order status:', err);
      showSnackbar(err?.data?.message || 'Error updating order status', 'error');
      setIsUpdating(false);
    }
  };

  // Handle clear/reset
  const handleClear = () => {
    setOrderId('');
    setSelectedOrder(null);
    setProcessingOrder(null);
    
    // Focus on the input field
    setTimeout(() => {
      if (orderIdInputRef.current) {
        orderIdInputRef.current.focus();
      }
    }, 50);
  };

  // Handle manual search
  const handleManualSearch = () => {
    if (orderId && orderId.length >= 1) {
      fetchOrderDetails(orderId);
    }
  };

  // Render product image
  const renderProductImage = (item: any) => {
    const imageUrl = getProductImageUrl(item.product_image || item.product_main_image);
    
    return (
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: 1,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
          border: '1px solid #e0e0e0',
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={item.product_name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              img.style.display = 'none';
            }}
          />
        ) : (
          <ImageIcon sx={{ color: '#bdbdbd', fontSize: 32 }} />
        )}
      </Box>
    );
  };

  // Copy order ID to clipboard
  const handleCopyOrderId = () => {
    if (orderId) {
      navigator.clipboard.writeText(orderId);
      showSnackbar('Order ID copied to clipboard', 'info');
    }
  };

  // Show snackbar
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Render order details
  const renderOrderDetails = (order: Order, isProcessing: boolean = false) => {
    return (
      <Fade in={true} timeout={500}>
        <Card sx={{ 
          p: 3, 
          mt: 3, 
          borderRadius: 2, 
          boxShadow: 3, 
          bgcolor: '#ffffff',
          border: isProcessing ? '2px solid #ff9800' : 'none'
        }}>
          {isProcessing && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff9800', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Search /> Processing New Order #{order.id}
              </Typography>
            </Box>
          )}
          
          {/* Status Update Progress */}
          {isProcessing && isUpdating && (
            <Box sx={{ mb: 3 }}>
              <LinearProgress sx={{ height: 8, borderRadius: 4 }} />
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                Updating to Pickup Pending...
              </Typography>
            </Box>
          )}
          
          {/* Order Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#1976d2' }}>
                Order #{order.id}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <QrCode fontSize="small" />
                {formatDate(order.order_date)}
                {order.order_type && (
                  <Chip 
                    label={order.order_type.toUpperCase()} 
                    size="small" 
                    color={order.order_type === 'guest' ? 'warning' : 'primary'}
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
            </Box>
            
            <Chip
              label={getStatusText(order.status)}
              icon={<LocalShipping />}
              sx={{
                backgroundColor: getStatusColor(order.status),
                color: 'white',
                fontWeight: 700,
                fontSize: '14px',
                padding: '10px 16px',
                height: 'auto',
                minHeight: '40px',
              }}
            />
          </Box>

          {/* Status Update Confirmation */}
          {!isProcessing && order.status === 3 && (
            <Box sx={{ mb: 3, p: 3, bgcolor: '#e8f5e9', borderRadius: 2, border: '2px solid #4caf50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircle sx={{ color: '#4caf50', fontSize: 40 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32', mb: 0.5 }}>
                    ✓ Successfully Updated to Pickup Pending!
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Order #{order.id} is now ready for warehouse pickup
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Cursor is focused in input field. Ready for next scan.
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Customer & Summary Grid */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Customer Info */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%', bgcolor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#495057', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person /> Customer Information
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Customer Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {order.user_name}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">User ID</Typography>
                    <Typography variant="body1">{order.user_id}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Order Type</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {order.order_type === 'guest' ? 'Guest Order' : 'Regular Order'}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Delivery Agent */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%', bgcolor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#495057', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalShipping /> Delivery Agent
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Agent Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#ff6f00' }}>
                      {getDeliveryAgentInfo(order)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Delivery Address ID</Typography>
                    <Typography variant="body1">{order.delivery_address_id}</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Order Summary */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%', bgcolor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#495057', display: 'flex', alignItems: 'center', gap: 1 }}>
                   Order Summary
                </Typography>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                    <Typography variant="body1">{formatCurrency(order.total_amount)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Discount:</Typography>
                    <Typography variant="body1" color="error">-{formatCurrency(order.total_discount_amount)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Shipping:</Typography>
                    <Typography variant="body1">{formatCurrency(order.total_shipping_charge)}</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Total:</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2' }}>
                      {formatCurrency(order.total_order_amount)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Order Items */}
          {order.items && order.items.length > 0 && (
            <Paper sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#495057', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Store /> Order Items ({order.items.length})
              </Typography>
              <TableContainer sx={{ borderRadius: 1 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e9ecef' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Warehouse</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.items.map((item: any, index: number) => (
                      <TableRow key={index} sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {renderProductImage(item)}
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {item.product_name || `Product ${item.product_id}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {item.product_id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getWarehouseName(item)} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            icon={<Store fontSize="small" />}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={item.product_quantity || 1} 
                            size="small"
                            color="secondary"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500 }}>
                          {formatCurrency(item.unit_price)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          {formatCurrency(item.itemTotal)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Payment Information */}
          <Paper sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#495057' }}>
              Payment Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Payment Method:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {order.cash_on_delivery === 1 ? 'Cash on Delivery' : 
                     order.pay_later === 1 ? 'Pay Later' : 'Online Payment'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Payment Status:</Typography>
                  <Chip 
                    label={order.payment_status || 'Pending'} 
                    size="small"
                    color={order.payment_status === 'Paid' ? 'success' : 'warning'}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Action Button for completed orders */}
          {!isProcessing && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleClear}
                startIcon={<Close />}
                sx={{
                  textTransform: 'none',
                  py: 1.5,
                  px: 4,
                  fontSize: '16px',
                  fontWeight: 600,
                  borderRadius: 2,
                  bgcolor: '#757575',
                  '&:hover': { bgcolor: '#616161' },
                }}
              >
                Clear All
              </Button>
            </Box>
          )}
        </Card>
      </Fade>
    );
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Sidebar - Fixed */}
      <Box 
        sx={{ 
          width: '250px', 
          flexShrink: 0,
          position: 'fixed',
          height: '100vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          zIndex: 1,
        }}
      >
        <Slidemenu mobileOpen={false} onToggle={() => {}} />
      </Box>

      {/* Main Content Area - Scrollable */}
      <Box 
        sx={{ 
          flexGrow: 1,
          ml: '250px', // Match sidebar width
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <AdminHeader onToggleMenu={() => {}} />
        
        {/* Scrollable Content */}
        <Box 
          sx={{ 
            flexGrow: 1,
            p: { xs: 2, md: 3 },
            maxWidth: '1200px',
            width: '100%',
            mx: 'auto',
            overflowY: 'auto',
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#1976d2', mb: 1 }}>
              Pickup Pending Scanner
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
              Scan order ID - Automatically updates to Pickup Pending
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Previous scanned order stays displayed. Cursor automatically focuses in input field.
            </Typography>
          </Box>

          {/* Order ID Input */}
          <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: 3, mb: 4, bgcolor: 'white' }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#495057' }}>
              Scan Order ID
            </Typography>
            
            <Box sx={{ maxWidth: 500, mx: 'auto' }}>
              <TextField
                fullWidth
                size="medium"
                placeholder="Scan order ID here (e.g., 1234)"
                value={orderId}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setOrderId(value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && orderId) {
                    handleManualSearch();
                  }
                }}
                disabled={isLoadingOrder || isUpdating}
                inputRef={orderIdInputRef}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#6c757d' }} />
                    </InputAdornment>
                  ),
                  endAdornment: orderId && (
                    <InputAdornment position="end">
                      <IconButton 
                        size="small" 
                        onClick={handleCopyOrderId} 
                        title="Copy"
                        disabled={isLoadingOrder || isUpdating}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => setOrderId('')}
                        disabled={isLoadingOrder || isUpdating}
                      >
                        <Close />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={handleManualSearch}
                        disabled={isLoadingOrder || isUpdating || !orderId}
                        sx={{ ml: 1 }}
                      >
                        <Search />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  bgcolor: 'white',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    py: 0.5,
                    '&.Mui-focused': {
                      borderColor: '#1976d2',
                      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
                onFocus={(e) => e.target.select()}
              />
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                {orderId 
                  ? `Scanning: ${orderId}` 
                  : selectedOrder 
                    ? `Last Scanned: Order #${selectedOrder.id} - Ready for next scan` 
                    : 'Ready to scan - Input field is focused'}
              </Typography>
              
              {(isLoadingOrder || isLoadingOrders) && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <CircularProgress size={24} sx={{ color: '#1976d2' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {isLoadingOrders ? 'Loading orders...' : `Finding order #${orderId}...`}
                  </Typography>
                </Box>
              )}
              
              {orderIdInputRef.current && orderIdInputRef.current === document.activeElement && (
                <Typography variant="caption" color="success.main" sx={{ mt: 1, textAlign: 'center', display: 'block' }}>
                  ✓ Cursor is in input field (ready to scan)
                </Typography>
              )}
            </Box>
          </Card>

          {/* Current Order Details */}
          {processingOrder ? (
            renderOrderDetails(processingOrder, true)
          ) : selectedOrder ? (
            renderOrderDetails(selectedOrder, false)
          ) : (
            <Card sx={{ p: 6, textAlign: 'center', borderRadius: 3, boxShadow: 1, bgcolor: 'white', mt: 2 }}>
              <QrCode sx={{ fontSize: 80, color: '#1976d2', mb: 3 }} />
              <Typography variant="h5" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                Ready to Scan
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '500px', mx: 'auto', mb: 2 }}>
                The scanner input field is focused and ready. Scan an order ID to automatically process.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Total orders loaded: {allOrders.length}
              </Typography>
            </Card>
          )}
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            fontSize: '16px',
            fontWeight: 500,
            borderRadius: 2,
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PickupPending;